/* eslint-disable brace-style */
/* eslint-disable import/no-named-as-default-member -- agentkeepalive: HttpsAgent on default export (CJS interop) */
/* eslint-disable security/detect-object-injection */
import Agent from 'agentkeepalive'
import axios, { AxiosError } from 'axios'
import { GetServerSidePropsContext } from 'next'

import {
  HTTP_STATUS_INTERNAL_SERVER_ERROR,
  HTTP_STATUS_NOT_FOUND,
} from '../utils/constants'

const httpAgent = new Agent({
  keepAlive: true,
  maxSockets: 100,
  maxFreeSockets: 10,
  timeout: 60000,
  freeSocketTimeout: 30000,
})

const httpsAgent = new Agent.HttpsAgent({
  keepAlive: true,
  maxSockets: 100,
  maxFreeSockets: 10,
  timeout: 60000,
  freeSocketTimeout: 30000,
})

const client = axios.create({
  httpAgent,
  httpsAgent,
  timeout: 10000, // 10 second timeout for Keystone calls
})

export interface KeystoneConfiguration {
  key: string
  version?: number
  createdAt?: string
  createdBy?: string
  data?: {
    default?: {
      tokens?: string[]
      [key: string]: any
    }
    [key: string]: any
  }
}

export type KeystoneConfigurationsResponse = KeystoneConfiguration[]

/**
 * Fetches configurations from Keystone API
 * @param context - Next.js server-side props context
 * @returns Keystone configurations array or null on error
 */
export async function fetchKeystoneConfigurations(
  context: GetServerSidePropsContext
): Promise<KeystoneConfigurationsResponse | null> {
  const keystoneUrl = process.env.KEYSTONE_URL

  if (!keystoneUrl) {
    console.error('KEYSTONE_URL environment variable is not set')
    return null
  }

  const url = `${keystoneUrl}/api/configurations/render-tokens`
  const cookieHeader = context.req.headers.cookie || ''
  const apiKey = process.env.KEYSTONE_API_KEY

  try {
    const response = await client.get<KeystoneConfiguration>(url, {
      headers: {
        cookie: cookieHeader,
        ...(apiKey && { Authorization: `Bearer ${apiKey}` }),
      },
    })

    if (!response.data || typeof response.data !== 'object') {
      console.error('Keystone API returned invalid response format', {
        url,
        responseType: typeof response.data,
      })
      return null
    }

    return [{ key: 'render-tokens', ...response.data }]
  } catch (error) {
    const axiosError = error as AxiosError
    const status = axiosError.response?.status
    const message = axiosError.message

    if (status === HTTP_STATUS_INTERNAL_SERVER_ERROR) {
      console.error('Keystone API returned 500 error', { url, status })
    } else if (status === HTTP_STATUS_NOT_FOUND) {
      console.error('Keystone configurations endpoint not found', {
        url,
        status,
      })
    } else if (axiosError.code === 'ECONNABORTED' || message.includes('timeout')) {
      console.error('Keystone API request timed out', {
        url,
        timeout: 10000,
      })
    } else {
      console.error('Failed to fetch Keystone configurations', {
        url,
        status,
        message,
      })
    }

    return null
  }
}

/**
 * Gets the render-tokens configuration from Keystone configurations array
 * @param configurations - Array of Keystone configurations
 * @returns The render-tokens configuration or null if not found
 */
export function getRenderTokensConfiguration(
  configurations: KeystoneConfigurationsResponse | null
): KeystoneConfiguration | null {
  if (!configurations || !Array.isArray(configurations)) {
    return null
  }

  return configurations.find((config) => config.key === 'render-tokens') || null
}
